import React, { useEffect, useState } from 'react';
import { supabase, supabaseStorage } from '../services/supabase';

type Entry = {
  name: string;
  isFolder: boolean;
  size?: number;
  createdAt?: string;
};

const BUCKET_NAME = 'assets';
const DEFAULT_FOLDER = 'products';

const StorageDiagnostics: React.FC = () => {
  const [rootEntries, setRootEntries] = useState<Entry[]>([]);
  const [assetsEntries, setAssetsEntries] = useState<Entry[]>([]);
  const [assetsProductsEntries, setAssetsProductsEntries] = useState<Entry[]>([]);
  const [deepImages, setDeepImages] = useState<Array<{ name: string; url: string; size: number; createdAt: string }>>([]);

  const [rootError, setRootError] = useState<string>('');
  const [assetsError, setAssetsError] = useState<string>('');
  const [assetsProductsError, setAssetsProductsError] = useState<string>('');
  const [deepError, setDeepError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const classifyEntry = (asAny: any): Entry => {
    const name: string = asAny?.name || '';
    const hasDot = name.includes('.');
    const hasSize = Number.isFinite(asAny?.metadata?.size);
    const isFolder = (asAny?.id === null || typeof asAny?.id === 'undefined') || (!hasDot && !hasSize);
    const size = hasSize ? Number(asAny?.metadata?.size) : undefined;
    const createdAt = asAny?.created_at || undefined;
    return { name, isFolder, size, createdAt };
  };

  const listAt = async (path: string): Promise<{ entries: Entry[]; error?: string }> => {
    try {
      const { data, error } = await supabase.storage.from(BUCKET_NAME).list(path, { limit: 100, offset: 0 });
      if (error) {
        return { entries: [], error: error.message || String(error) };
      }
      const entries = (data || []).map((e: any) => classifyEntry(e));
      return { entries };
    } catch (err: any) {
      return { entries: [], error: err?.message || String(err) };
    }
  };

  const runDiagnostics = async () => {
    setLoading(true);
    setRootError('');
    setAssetsError('');
    setAssetsProductsError('');
    setDeepError('');
    try {
      const root = await listAt('');
      setRootEntries(root.entries);
      if (root.error) setRootError(root.error);

      const assets = await listAt('assets');
      setAssetsEntries(assets.entries);
      if (assets.error) setAssetsError(assets.error);

      const assetsProducts = await listAt(DEFAULT_FOLDER);
      setAssetsProductsEntries(assetsProducts.entries);
      if (assetsProducts.error) setAssetsProductsError(assetsProducts.error);

      const deep = await supabaseStorage.listImagesDeep(DEFAULT_FOLDER);
      if ('success' in deep && deep.success) {
        setDeepImages(deep.images);
      } else {
        setDeepImages([]);
        setDeepError((deep as any)?.error || 'Error desconocido al listar imágenes');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    runDiagnostics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold mb-4">Diagnóstico de Supabase Storage</h1>
      <div className="mb-6 space-y-2">
        <p><strong>Bucket:</strong> {BUCKET_NAME}</p>
        <p><strong>Carpeta por defecto:</strong> {DEFAULT_FOLDER}</p>
        <button
          onClick={runDiagnostics}
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? 'Ejecutando…' : 'Reintentar diagnóstico'}
        </button>
      </div>

      <section className="mb-8">
        <h2 className="text-xl font-medium mb-2">Listado raíz del bucket</h2>
        {rootError && <p className="text-red-600">Error: {rootError}</p>}
        {!rootError && rootEntries.length === 0 && <p className="text-gray-600">Sin entradas en la raíz.</p>}
        <ul className="list-disc ml-6">
          {rootEntries.map((e) => (
            <li key={`root-${e.name}`}>{e.isFolder ? '📁' : '📄'} {e.name}</li>
          ))}
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-medium mb-2">Listado en "assets"</h2>
        {assetsError && <p className="text-red-600">Error: {assetsError}</p>}
        {!assetsError && assetsEntries.length === 0 && <p className="text-gray-600">Sin entradas en assets.</p>}
        <ul className="list-disc ml-6">
          {assetsEntries.map((e) => (
            <li key={`assets-${e.name}`}>{e.isFolder ? '📁' : '📄'} {e.name}</li>
          ))}
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-medium mb-2">Listado en "{DEFAULT_FOLDER}"</h2>
        {assetsProductsError && <p className="text-red-600">Error: {assetsProductsError}</p>}
        {!assetsProductsError && assetsProductsEntries.length === 0 && <p className="text-gray-600">Sin entradas en {DEFAULT_FOLDER}.</p>}
        <ul className="list-disc ml-6">
          {assetsProductsEntries.map((e) => (
            <li key={`assets-products-${e.name}`}>{e.isFolder ? '📁' : '📄'} {e.name}</li>
          ))}
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-medium mb-2">Imágenes detectadas (búsqueda recursiva)</h2>
        {deepError && <p className="text-red-600">Error: {deepError}</p>}
        {!deepError && deepImages.length === 0 && <p className="text-gray-600">No se encontraron imágenes en {DEFAULT_FOLDER}.</p>}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {deepImages.map((img) => (
            <div key={img.name} className="border rounded p-2">
              <img src={img.url} alt={img.name} className="w-full h-32 object-cover rounded" />
              <div className="mt-2 text-sm text-gray-700">
                <div className="font-mono break-all">{img.name}</div>
                <div>Tamaño: {img.size} bytes</div>
                {img.createdAt && <div>Creado: {new Date(img.createdAt).toLocaleString()}</div>}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default StorageDiagnostics;